import { Modal as MantineModal, type ModalProps as MantineModalProps } from "@mantine/core";
import clsx from "clsx";
import { type ReactNode } from "react";
import styles from "./Modal.module.scss";

export interface ModalProps extends Omit<MantineModalProps, "opened" | "onClose"> {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  centered?: boolean;
  withCloseButton?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  size?: MantineModalProps["size"] | number;
  /** Optional extra class applied to the Mantine content element */
  contentClassName?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  centered = true,
  withCloseButton = true,
  closeOnClickOutside = false,
  closeOnEscape = false,
  className,
  size,
  contentClassName,
  zIndex,
  ...rest
}: ModalProps) {
  // pull out classNames & portalProps if caller passes them
  const {
    classNames: customClassNames = {},
    portalProps: userPortalProps,
    ...modalRest
  } = rest as any;

  // Do not touch the portal props below.
  // Refer to the styles.scss file for the styles applied to the portal.
  const mergedPortalProps = {
    target: "#mantine-modals",
    ...(userPortalProps || {}),
    style: {
      ...(userPortalProps?.style || {}),
      // only pure values here – no !important
      margin: 0,
      padding: 0,
      height: 0,
      minHeight: 0,
      border: 0,
    },
  };
  // Refer to the styles.scss file for the styles applied to the portal.
  // Do not touch the portal props above.

  return (
    <MantineModal
      {...modalRest}
      opened={isOpen}
      onClose={onClose}
      size={size}
      title={title}
      centered={centered}
      withCloseButton={withCloseButton}
      closeOnClickOutside={closeOnClickOutside}
      closeOnEscape={closeOnEscape}
      zIndex={zIndex}
      withinPortal
      portalProps={mergedPortalProps}
      classNames={{
        title: clsx(styles.title, className, customClassNames.title),
        header: clsx(styles.header, customClassNames.header),
        body: clsx(styles.body, customClassNames.body),
        content: clsx(styles.content, contentClassName, customClassNames.content),
        ...customClassNames,
      }}
    >
      {children}
    </MantineModal>
  );
}
